export function createFukushizaiCopyMutationService(deps) {
    // 品質表示の複写対象 content 列を返します。
    function getQualityCopyContentColumnIds(panelType) {
        const type = deps.canonicalPanelType(panelType);
        const map = deps.getQualityPanelFieldMap(type) || {};
        const excluded = new Set([
            deps.ROW_ID_FIELD,
            "INDEX",
            "GRIP",
            "EXPAND",
            "ROW_KIND",
            String(deps.DicVal(map, "no", "") ?? ""),
            String(deps.DicVal(map, "item", "") ?? ""),
            String(deps.DicVal(map, "group", "") ?? ""),
            String(deps.DicVal(map, "groupFallback", "") ?? ""),
            String(deps.DicVal(map, "color", "") ?? ""),
            String(deps.DicVal(map, "invalid", "") ?? "")
        ].map((fieldId) => String(fieldId ?? "").trim().toUpperCase()).filter((fieldId) => fieldId !== ""));

        const contentIds = [...deps.getSaveableColumnIdSetByPanelType(type)]
            .map((fieldId) => String(fieldId ?? "").trim())
            .filter((fieldId) => {
                const cid = fieldId.toUpperCase();
                return cid !== "" && !excluded.has(cid) && !deps.isKakousijiSavePayloadUiOnlyField(fieldId, type);
            });

        // 洗濯表示(124)は洗濯マークをM_STP由来の表示専用列(STP_SENTAKU1-7/mark1-7)から描画するため、
        // 物理列(SSDE_STPCD/SSDE_PATANKBN)だけを複写すると画面上は空表示になる。
        // 表示用列も複写対象へ加える(保存時は表示専用列として除去されるため保存へは影響しない)。
        if (type === String(deps.WASH_PANEL_TYPE ?? "") && Array.isArray(deps.WASH_COPY_DISPLAY_FIELD_IDS)) {
            return [...new Set([...contentIds, ...deps.WASH_COPY_DISPLAY_FIELD_IDS])];
        }
        return contentIds;
    }

    // 品質表示の複写元 row は代表 color 1 色分へ絞ります。
    function representativeQualityRowsForCopy(panelType, sourceRowsForContext) {
        const rows = (Array.isArray(sourceRowsForContext) ? sourceRowsForContext : [])
            .filter((row) => !deps.isEmpty(row) && !deps.isInvalidDisplayRow(row, panelType));
        if (rows.length === 0) return [];

        const firstColor = deps.readFukushizaiItemGroupFromAnyRow(rows[0]).colorCd;
        if (firstColor === "") return rows;
        return rows.filter((row) => deps.readFukushizaiItemGroupFromAnyRow(row).colorCd === firstColor);
    }

    // 副資材 base 内容を target colors へ複写します。
    function mergeFukushizaiBaseContentIntoTargetColors(itemCd, groupNo, sourceRowsForContext, targetColorCodes) {
        const targetItem = deps.normalizeItemCd(itemCd);
        const targetGroup = deps.normalizeGroupNo(groupNo);
        const targetColors = [...new Set((Array.isArray(targetColorCodes) ? targetColorCodes : [])
            .map((value) => deps.normalizeColorCd(value))
            .filter((value) => value !== ""))];
        if (targetItem === "" || targetGroup === "" || targetColors.length === 0) return false;

        const sourceRow = (Array.isArray(sourceRowsForContext) ? sourceRowsForContext : [])
            .find((row) => !deps.isEmpty(row) && !deps.isInvalidDisplayRow(row, deps.FUKUSHIZAI_BASE_PANEL_TYPE));
        if (deps.isEmpty(sourceRow)) return false;

        const detail = deps.ensureDetailRowsByTypeForMutation(deps.FUKUSHIZAI_BASE_PANEL_TYPE);
        const rows = Array.isArray(detail.rows) ? detail.rows : [];
        const contentColumnIds = deps.getFukushizaiBaseCopyContentColumnIds();
        let changed = false;

        targetColors.forEach((colorCd) => {
            let index = rows.findIndex((row) => deps.sameItemGroupColor(row, targetItem, targetGroup, colorCd));
            if (index < 0) {
                const created = deps.createFukushizaiGroupDetailRow(targetItem, targetGroup, colorCd, null, false);
                if (deps.isEmpty(created)) return;
                rows.push(created);
                index = rows.length - 1;
                changed = true;
            }

            const current = rows[index];
            const next = deps.copyFukushizaiContentColumns(current, sourceRow, contentColumnIds, deps.FUKUSHIZAI_BASE_PANEL_TYPE);
            deps.setRowContextForPanel(next, deps.FUKUSHIZAI_BASE_PANEL_TYPE, targetItem, targetGroup, colorCd);
            delete next.SSDZ_SONOTAFUZOKUCD6;

            if (JSON.stringify(current) !== JSON.stringify(next)) {
                rows[index] = next;
                changed = true;
            }
        });

        if (changed) deps.entrydetailRef.current[detail.key] = rows;
        return changed;
    }

    // 品質表示 rows を target colors 向けに置換します。
    function replaceQualityRowsForContext(panelType, itemCd, groupNo, sourceRowsForContext, targetColorCodes = []) {
        const detail = deps.ensureDetailRowsByTypeForMutation(panelType);
        const invalidField = deps.getQualityInvalidField(panelType);
        const targetItem = deps.normalizeItemCd(itemCd);
        const targetGroup = deps.normalizeGroupNo(groupNo);
        const targetColors = [...new Set((Array.isArray(targetColorCodes) ? targetColorCodes : [])
            .map((value) => deps.normalizeColorCd(value))
            .filter((value) => value !== ""))];
        if (targetItem === "" || targetGroup === "" || targetColors.length === 0) return false;

        const targetColorSet = new Set(targetColors);
        let changed = false;
        let nextRows = detail.rows.map((row) => {
            if (!deps.rowMatchesFukushizaiItemGroup(row, targetItem, targetGroup)) return row;
            if (!targetColorSet.has(deps.readFukushizaiItemGroupFromAnyRow(row).colorCd)) return row;
            if (invalidField === "") return row;
            if (String(deps.readRowValueIgnoreCase(row, invalidField) ?? "").trim() === "1") return row;
            changed = true;
            return { ...row, [invalidField]: "1" };
        });

        let nextRowId = deps.nextDetailRowIdForRows(nextRows);
        const sourceRows = representativeQualityRowsForCopy(panelType, sourceRowsForContext);
        const contentColumnIds = getQualityCopyContentColumnIds(panelType);

        targetColors.forEach((colorCd) => {
            sourceRows.forEach((sourceRow) => {
                const next = deps.copyFukushizaiContentColumns({}, sourceRow, contentColumnIds, panelType);
                next[deps.ROW_ID_FIELD] = String(nextRowId++);
                deps.setRowContextForPanel(next, panelType, targetItem, targetGroup, colorCd);
                nextRows.push(next);
                changed = true;
            });
        });

        if (changed) deps.entrydetailRef.current[detail.key] = nextRows;
        return changed;
    }

    // group 編集時に base rows を色一覧ごと再構築します。
    function rebuildFukushizaiBaseRowsForEditedGroup(itemCd, groupNo, sourceRowsForContext, targetColorCodes = []) {
        const detail = deps.ensureDetailRowsByTypeForMutation(deps.FUKUSHIZAI_BASE_PANEL_TYPE);
        const targetItem = deps.normalizeItemCd(itemCd);
        const targetGroup = deps.normalizeGroupNo(groupNo);
        const targetColors = [...new Set((Array.isArray(targetColorCodes) ? targetColorCodes : [])
            .map((value) => deps.normalizeColorCd(value))
            .filter((value) => value !== ""))];
        if (targetItem === "" || targetGroup === "") return false;

        const rows = Array.isArray(detail.rows) ? detail.rows : [];
        const sourceRow = (Array.isArray(sourceRowsForContext) ? sourceRowsForContext : [])
            .find((row) => !deps.isEmpty(row) && !deps.isInvalidDisplayRow(row, deps.FUKUSHIZAI_BASE_PANEL_TYPE));
        let changed = false;
        const nextRows = rows.filter((row) => {
            const context = deps.readFukushizaiItemGroupFromAnyRow(row);
            if (context.itemCd === targetItem && context.groupNo === targetGroup) {
                changed = true;
                return false;
            }
            return true;
        });

        targetColors.forEach((colorCd) => {
            const next = deps.createFukushizaiGroupDetailRow(targetItem, targetGroup, colorCd, sourceRow, !deps.isEmpty(sourceRow));
            if (deps.isEmpty(next)) return;
            nextRows.push(next);
            changed = true;
        });

        if (changed) deps.entrydetailRef.current[detail.key] = nextRows;
        return changed;
    }

    // group 編集時に品質表示 rows を色一覧ごと再構築します。
    function rebuildQualityRowsForEditedGroup(panelType, itemCd, groupNo, sourceRowsForContext, targetColorCodes = []) {
        const type = deps.canonicalPanelType(panelType);
        const detail = deps.ensureDetailRowsByTypeForMutation(type);
        const invalidField = deps.getQualityInvalidField(type);
        const targetItem = deps.normalizeItemCd(itemCd);
        const targetGroup = deps.normalizeGroupNo(groupNo);
        const targetColors = [...new Set((Array.isArray(targetColorCodes) ? targetColorCodes : [])
            .map((value) => deps.normalizeColorCd(value))
            .filter((value) => value !== ""))];
        if (targetItem === "" || targetGroup === "") return false;

        const rows = Array.isArray(detail.rows) ? detail.rows : [];
        let changed = false;
        const nextRows = [];

        rows.forEach((row) => {
            const context = deps.readFukushizaiItemGroupFromAnyRow(row);
            if (context.itemCd !== targetItem || context.groupNo !== targetGroup) {
                nextRows.push(row);
                return;
            }
            if (invalidField === "") {
                changed = true;
                return;
            }
            if (deps.isInvalidDisplayRow(row, type)) {
                nextRows.push(row);
                return;
            }
            nextRows.push({ ...row, [invalidField]: "1" });
            changed = true;
        });

        let nextRowId = deps.nextDetailRowIdForRows(nextRows);
        const sourceRows = representativeQualityRowsForCopy(type, sourceRowsForContext);
        const contentColumnIds = getQualityCopyContentColumnIds(type);

        targetColors.forEach((colorCd) => {
            sourceRows.forEach((sourceRow) => {
                const next = deps.copyFukushizaiContentColumns({}, sourceRow, contentColumnIds, type);
                next[deps.ROW_ID_FIELD] = String(nextRowId++);
                deps.setRowContextForPanel(next, type, targetItem, targetGroup, colorCd);
                nextRows.push(next);
                changed = true;
            });
        });

        if (changed) deps.entrydetailRef.current[detail.key] = nextRows;
        return changed;
    }

    // 追加された colors へ品質表示 rows を展開します。
    function expandQualityRowsToAddedColors(itemCd, groupNo, selectedColorCodes) {
        let changed = false;
        deps.QUALITY_DISPLAY_PANEL_TYPES.forEach((panelType) => {
            const detail = deps.ensureDetailRowsByTypeForMutation(panelType);
            const result = deps.expandRowsToAddedColors(detail.rows, panelType, itemCd, groupNo, selectedColorCodes);
            if (!result.changed) return;
            deps.entrydetailRef.current[detail.key] = result.rows;
            changed = true;
        });
        return changed;
    }

    // group copy 本体を state へ反映します。
    function applyFukushizaiGroupCopyToState(targetContext, sourceContext) {
        const targetItem = deps.normalizeItemCd(deps.DicVal(targetContext, "itemCd", ""));
        const targetGroup = deps.normalizeGroupNo(deps.DicVal(targetContext, "groupNo", ""));
        const sourceItem = deps.normalizeItemCd(deps.DicVal(sourceContext, "itemCd", ""));
        const sourceGroup = deps.normalizeGroupNo(deps.DicVal(sourceContext, "groupNo", ""));
        if (targetItem === "" || targetGroup === "" || sourceItem === "" || sourceGroup === "") return false;
        if (targetItem === sourceItem && targetGroup === sourceGroup) return false;

        const targetColors = deps.getFukushizaiCopyTargetColorCodes(targetItem, targetGroup);
        if (targetColors.length === 0) return false;

        const sourceBaseRows = deps.sourceRowsForCopy(deps.FUKUSHIZAI_BASE_PANEL_TYPE, sourceItem, sourceGroup);
        if (sourceBaseRows.length === 0) return false;

        // 副資材(113)の内容のみ複写する。
        // 品質表示(118-124)は副資材と独立したグループ管理のため、複写対象に含めない
        // (含めると複写先グループが品質側のグループ候補にも出現し、分割管理が壊れる)。
        let changed = mergeFukushizaiBaseContentIntoTargetColors(targetItem, targetGroup, sourceBaseRows, targetColors);
        if (deps.updateSagefudaItemFlags([targetItem])) changed = true;

        deps.debugFukushizaiSyncLog("[Kakousiji][fukushizai group copy applied]", {
            sourceItem,
            sourceGroup,
            sourceColors: deps.fukushizaiColorCodesForContext(sourceItem, sourceGroup),
            targetItem,
            targetGroup,
            targetColors,
            copiedBaseRowsCount: sourceBaseRows.length
        });

        return changed;
    }

    return {
        mergeFukushizaiBaseContentIntoTargetColors,
        getQualityCopyContentColumnIds,
        representativeQualityRowsForCopy,
        replaceQualityRowsForContext,
        rebuildFukushizaiBaseRowsForEditedGroup,
        rebuildQualityRowsForEditedGroup,
        expandQualityRowsToAddedColors,
        applyFukushizaiGroupCopyToState
    };
}
